import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Info } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth, formatApiError } from "@/auth/AuthContext";

const Auth = ({ mode }) => {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const isLogin = mode === "login";
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(form.email, form.password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Login failed. Check your credentials."));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-slate-800/70 border border-slate-700 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors";

  return (
    <div data-testid={`${mode}-page`} className="min-h-[80vh] flex items-center justify-center px-5 py-20 grid-bg relative">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] glow-orb pointer-events-none" />
      <div className="w-full max-w-md relative">
        <div className="glass rounded-3xl p-8 sm:p-10">
          <div className="flex justify-center mb-6">
            <img src="/logo-full.png" alt="OTOSUBS" className="h-16 w-auto" />
          </div>
          <h1 className="text-2xl font-bold font-heading text-white text-center mb-2">{isLogin ? t("auth.loginTitle") : t("auth.signupTitle")}</h1>
          <p className="text-sm text-slate-400 text-center mb-8">{isLogin ? t("auth.loginSub") : t("auth.signupSub")}</p>

          {isLogin ? (
            <form onSubmit={submit} className="space-y-4">
              <input data-testid="auth-email" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder={t("auth.email")} className={inputCls} />
              <input data-testid="auth-password" type="password" required value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder={t("auth.password")} className={inputCls} />
              {error && <p data-testid="auth-error" className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-3">{error}</p>}
              <button type="submit" data-testid="auth-submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white font-semibold px-6 py-3.5 text-sm transition-colors shadow-lg shadow-indigo-500/30">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("auth.loginBtn")}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-2.5 rounded-xl bg-indigo-500/10 border border-indigo-500/30 px-4 py-4">
                <Info className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" />
                <p className="text-xs text-indigo-200/90 leading-relaxed">
                  {t("common.demoMode")} — public sign-up opens at launch. Book a demo and our team will create your organisation account.
                </p>
              </div>
              <Link to="/contact" data-testid="signup-contact" className="block text-center w-full rounded-full bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-6 py-3.5 text-sm transition-colors shadow-lg shadow-indigo-500/30">
                {t("common.bookDemo")}
              </Link>
            </div>
          )}

          <p className="text-sm text-slate-400 text-center mt-6">
            {isLogin ? t("auth.noAccount") : t("auth.haveAccount")}{" "}
            <Link data-testid="auth-switch" to={isLogin ? "/signup" : "/login"} className="text-indigo-400 font-semibold hover:text-indigo-300">
              {isLogin ? t("nav.startFree") : t("nav.login")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
